import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// Categorías para filtrar
const categories = ['Eventos', 'Control de Accesos', 'Limpieza'];

// This array will hold the references to your images.
// Images should be placed in public/gallery/ folder.
const galleryImages = [
  { src: '/gallery/mercedes-evento-1.jpg', alt: 'Evento Mercedes-Benz - Vista General', category: 'Eventos' },
  { src: '/gallery/mercedes-evento-2.jpg', alt: 'Evento Mercedes-Benz - Control de Acceso', category: 'Eventos' },
  { src: '/gallery/mercedes-evento-3.jpg', alt: 'Evento Mercedes-Benz - Equipo de Control', category: 'Eventos' },
  { src: '/gallery/mercedes-evento-4.jpg', alt: 'Evento Mercedes-Benz - Supervisión', category: 'Eventos' },
  { src: '/gallery/control-acceso-1.jpeg', alt: 'Servicio de Control de Accesos 1', category: 'Control de Accesos' },
  { src: '/gallery/control-acceso-2.jpeg', alt: 'Servicio de Control de Accesos 2', category: 'Control de Accesos' },
  { src: '/gallery/control-acceso-3.jpeg', alt: 'Servicio de Control de Accesos 3', category: 'Control de Accesos' },
  { src: '/gallery/control-acceso-4.jpeg', alt: 'Servicio de Control de Accesos 4', category: 'Control de Accesos' },
  { src: '/gallery/limpieza-1.jpg', alt: 'Limpieza Industrial y de Grandes Superficies', category: 'Limpieza' },
  { src: '/gallery/limpieza-2.jpg', alt: 'Mantenimiento de Cristales y Fachadas', category: 'Limpieza' },
  { src: '/gallery/limpieza-3.jpg', alt: 'Limpieza de Detalle en Oficinas', category: 'Limpieza' },
  { src: '/gallery/limpieza-4.jpeg', alt: 'Desinfección y Protocolos Higiénicos', category: 'Limpieza' },
];

const Gallery = () => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [filter, setFilter] = useState('Eventos');

  // Filtrar imágenes según la categoría seleccionada
  const filteredImages = galleryImages.filter(
    (img) => img.category === filter
  );

  const openModal = (index: number) => {
    setSelectedIndex(index);
  };

  const closeModal = () => {
    setSelectedIndex(null);
  };

  const showNext = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (selectedIndex !== null) {
      setSelectedIndex((prev) => (prev !== null ? (prev + 1) % filteredImages.length : null));
    }
  }, [selectedIndex, filteredImages.length]);

  const showPrev = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (selectedIndex !== null) {
      setSelectedIndex((prev) => (prev !== null ? (prev - 1 + filteredImages.length) % filteredImages.length : null));
    }
  }, [selectedIndex, filteredImages.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedIndex === null) return;
      
      if (e.key === 'ArrowRight') {
        showNext();
      } else if (e.key === 'ArrowLeft') {
        showPrev();
      } else if (e.key === 'Escape') {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex, showNext, showPrev]);

  return (
    <div id="galeria" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Nuestra Experiencia</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Galería de Servicios
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
            Imágenes reales de nuestros equipos en acción.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                filter === category
                  ? 'bg-primary text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Infinite Scroll Container */}
        <div className="relative w-full overflow-hidden group">
          {/* Gradients - Hidden on mobile, visible on desktop */}
          <div className="hidden md:block absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
          <div className="hidden md:block absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

          <div key={filter} className="flex animate-scroll group-hover:paused">
            {/* Original Set */}
            {filteredImages.map((image, index) => (
              <div 
                key={`original-${index}`} 
                className="flex-shrink-0 w-[75vw] md:w-96 h-64 mx-2 md:mx-4 relative cursor-pointer overflow-hidden rounded-lg shadow-lg transform transition-transform duration-300 hover:scale-105"
                onClick={() => openModal(index)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="object-cover w-full h-full"
                  onError={(e) => {
                    e.currentTarget.src = `https://via.placeholder.com/400?text=Evento+${index + 1}`;
                  }}
                />
              </div>
            ))}

            {/* Duplicate Set for Loop */}
            {filteredImages.map((image, index) => (
              <div 
                key={`duplicate-${index}`} 
                className="flex-shrink-0 w-80 sm:w-96 h-64 mx-4 relative cursor-pointer overflow-hidden rounded-lg shadow-lg transform transition-transform duration-300 hover:scale-105"
                onClick={() => openModal(index)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="object-cover w-full h-full"
                  onError={(e) => {
                    e.currentTarget.src = `https://via.placeholder.com/400?text=Evento+${index + 1}`;
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-95 p-4" onClick={closeModal}>
          
          {/* Close Button */}
          <button 
            className="absolute top-4 right-4 z-50 p-2 text-white hover:text-gray-300 focus:outline-none bg-black bg-opacity-50 rounded-full"
            onClick={closeModal}
          >
            <X className="h-8 w-8" />
          </button>

          {/* Navigation Buttons */}
          <button
            className="absolute left-2 sm:left-6 z-50 p-2 text-white hover:bg-white/10 rounded-full transition-colors focus:outline-none"
            onClick={showPrev}
          >
            <ChevronLeft className="h-10 w-10 sm:h-12 sm:w-12" />
          </button>

          <button
            className="absolute right-2 sm:right-6 z-50 p-2 text-white hover:bg-white/10 rounded-full transition-colors focus:outline-none"
            onClick={showNext}
          >
            <ChevronRight className="h-10 w-10 sm:h-12 sm:w-12" />
          </button>

          {/* Image */}
          <img 
            src={filteredImages[selectedIndex].src} 
            alt={filteredImages[selectedIndex].alt} 
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg shadow-2xl select-none"
            onClick={(e) => e.stopPropagation()} 
          />
          
          {/* Counter text (optional) */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full flex flex-col items-center">
            <span className="font-semibold text-lg">{filteredImages[selectedIndex].category}</span>
            <span className="text-xs text-gray-300">{selectedIndex + 1} / {filteredImages.length}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;